import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { useForm, usePage } from '@inertiajs/react'
import { ArrowLeft, Save } from 'lucide-react'
import { Link } from '@inertiajs/react'

interface Community {
  id: number
  name: string
}

interface ContentType {
  value: string
  label: string
}

interface PageContentData {
  id?: number
  name: string
  content_type: string
  community_id: string
  target_page: string
  display_on_all_pages: boolean
  target_pages: string[]
}

interface PageContentFormProps {
  pageContent?: PageContentData
  communities: Community[]
  contentTypes: ContentType[]
}

const contentTypeLabels: Record<string, string> = {
  banner: 'Bannière (haut de page)',
  footer: 'Footer (pied de page)',
  events: 'Événements à venir'
}

const allPageOptions = [
  { value: 'homepage', label: 'Page d\'accueil' },
  { value: 'community', label: 'Page Communauté' },
  { value: 'content', label: 'Page Bibliothèque (Médecins)' },
  { value: 'faq', label: 'Page FAQ' },
  { value: 'defis', label: 'Page Défis (Pharmaciens)' },
  { value: 'boite-a-outil', label: 'Page Boite à outil (Pharmaciens)' },
  { value: 'boutique', label: 'Page Boutique (Médecins)' }
]

// Types qui supportent le ciblage multi-pages
const supportsPageTargeting = ['banner', 'footer']

export function PageContentForm({
  pageContent,
  communities,
  contentTypes
}: PageContentFormProps) {
  const { errors } = usePage().props as { errors?: Record<string, string[]> }
  const isEditing = Boolean(pageContent?.id)

  const { data, setData, post, put, processing } = useForm({
    name: pageContent?.name || '',
    content_type: pageContent?.content_type || 'banner',
    community_id: pageContent?.community_id || '',
    target_page: pageContent?.target_page || 'homepage',
    display_on_all_pages: pageContent?.display_on_all_pages || false,
    target_pages: pageContent?.target_pages || ['homepage']
  })

  // Détermine si ce type supporte le ciblage de pages
  const canTargetPages = supportsPageTargeting.includes(data.content_type)

  // Quand le type change, réinitialise les options de ciblage
  const handleContentTypeChange = (newType: string) => {
    setData(prev => ({
      ...prev,
      content_type: newType,
      display_on_all_pages: false,
      target_pages: ['homepage']
    }))
  }

  // Toggle une page dans target_pages
  const togglePage = (pageValue: string) => {
    const currentPages = data.target_pages || []
    if (currentPages.includes(pageValue)) {
      // Ne pas permettre de tout décocher
      if (currentPages.length > 1) {
        setData('target_pages', currentPages.filter(p => p !== pageValue))
      }
    } else {
      setData('target_pages', [...currentPages, pageValue])
    }
  }

  // Change le mode de ciblage
  const handleTargetingModeChange = (allPages: boolean) => {
    setData(prev => ({
      ...prev,
      display_on_all_pages: allPages,
      // Si on passe en mode spécifique, on garde les pages sélectionnées ou on met homepage par défaut
      target_pages: allPages ? [] : (prev.target_pages.length > 0 ? prev.target_pages : ['homepage'])
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isEditing && pageContent?.id) {
      put(`/admin/page_contents/${pageContent.id}`)
    } else {
      post('/admin/page_contents')
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-text-dark mb-4">
              Configuration
            </h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="content_type" className="block text-sm font-medium text-text-dark mb-1">
                  Type de contenu *
                </label>
                <Select
                  id="content_type"
                  value={data.content_type}
                  onChange={(e) => handleContentTypeChange(e.target.value)}
                  required
                >
                  {contentTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {contentTypeLabels[type.value] || type.label}
                    </option>
                  ))}
                </Select>
                <p className="mt-1 text-xs text-text-gray">
                  Choisissez le type de contenu à afficher
                </p>
              </div>

              {/* Page Targeting - Seulement pour banner et footer */}
              {canTargetPages ? (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-text-dark">
                    Affichage sur les pages *
                  </label>

                  {/* Radio buttons for targeting mode */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="targeting_mode"
                        checked={data.display_on_all_pages}
                        onChange={() => handleTargetingModeChange(true)}
                        className="w-4 h-4 text-tessan-green focus:ring-tessan-green"
                      />
                      <span className="text-sm text-text-dark">Toutes les pages</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="targeting_mode"
                        checked={!data.display_on_all_pages}
                        onChange={() => handleTargetingModeChange(false)}
                        className="w-4 h-4 text-tessan-green focus:ring-tessan-green"
                      />
                      <span className="text-sm text-text-dark">Pages spécifiques</span>
                    </label>
                  </div>

                  {/* Checkboxes for specific pages */}
                  {!data.display_on_all_pages && (
                    <div className="ml-6 p-3 bg-gray-50 rounded-lg space-y-2">
                      {allPageOptions.map((option) => (
                        <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={data.target_pages.includes(option.value)}
                            onChange={() => togglePage(option.value)}
                            className="w-4 h-4 rounded text-tessan-green focus:ring-tessan-green"
                          />
                          <span className="text-sm text-text-dark">{option.label}</span>
                        </label>
                      ))}
                      <p className="text-xs text-text-gray mt-2">
                        Sélectionnez au moins une page
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                /* Pour events: affiche juste un message statique */
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-text-gray">
                    <span className="font-medium">Page cible :</span> Page d'accueil (fixe pour ce type de contenu)
                  </p>
                </div>
              )}

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-text-dark mb-1">
                  Nom *
                </label>
                <Input
                  id="name"
                  type="text"
                  value={data.name}
                  onChange={(e) => setData('name', e.target.value)}
                  placeholder="Ex: Footer principal"
                  required
                />
                <p className="mt-1 text-xs text-text-gray">
                  Nom interne pour identifier ce contenu dans l'administration
                </p>
                {errors?.name && (
                  <p className="mt-1 text-sm text-error">{errors.name[0]}</p>
                )}
              </div>

              <div>
                <label htmlFor="community_id" className="block text-sm font-medium text-text-dark mb-1">
                  Communauté
                </label>
                <Select
                  id="community_id"
                  value={data.community_id}
                  onChange={(e) => setData('community_id', e.target.value)}
                >
                  <option value="">Global (toutes communautés)</option>
                  {communities.map((community) => (
                    <option key={community.id} value={community.id}>
                      {community.name}
                    </option>
                  ))}
                </Select>
                <p className="mt-1 text-xs text-text-gray">
                  Laissez vide pour un contenu visible sur toutes les communautés
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-text-dark mb-4">
              Actions
            </h2>
            <div className="space-y-3">
              <Button type="submit" className="w-full" disabled={processing}>
                <Save className="w-4 h-4 mr-2" />
                {processing ? 'Enregistrement...' : isEditing ? 'Mettre à jour' : 'Créer'}
              </Button>
              <Link href="/admin/page_contents" className="block">
                <Button type="button" variant="secondary" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
              </Link>
            </div>
          </Card>

          {/* Help */}
          <Card className="p-6 bg-tessan-cream/30">
            <h3 className="font-medium text-text-dark mb-2">Aide</h3>
            <p className="text-sm text-text-gray">
              {canTargetPages ? (
                <>
                  Vous pouvez afficher ce contenu sur toutes les pages ou seulement sur certaines pages spécifiques.
                  Après avoir créé ce contenu, vous pourrez l'éditer avec l'éditeur visuel.
                </>
              ) : (
                <>
                  Ce type de contenu s'affiche uniquement sur la page d'accueil.
                  Après avoir créé ce contenu, vous pourrez l'éditer avec l'éditeur visuel.
                </>
              )}
            </p>
          </Card>
        </div>
      </div>
    </form>
  )
}
